import React from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, Spacing, Text } from '@reservamos/elements';
import { When } from 'react-if';
import InfantDisclaimerText from './InfantDisclaimerText';

/**
 * PassengersStatements component.
 * Render both the passengers disclaimer and the passengers
 * instructions to upload documents.
 *
 * Only renders if features flags are enabled.
 * @returns {JSX.Element} The PassengersStatements component.
 */
const PassengersStatements = () => {
  const {
    features,
    env: { brand },
  } = useSelector((state) => state.whitelabelConfig);
  const passengers = useSelector((state) => state.form?.passengers?.values?.passengers || []);
  const { t } = useTranslation('passengers');

  const someWithoutGeneralCategory = passengers.some(
    (passenger) => passenger.busCategory && passenger.busCategory !== 'general',
  );

  return (
    <When
      condition={
        Boolean(features.PASSENGERS_SHOW_ID) ||
        Boolean(features.SHOW_INFANTS_DISCLAIMER) ||
        Boolean(features.SHOW_FOREIGN_DISCLAIMER)
      }
    >
      <Spacing vertical size="M">
        <When condition={Boolean(features.PASSENGERS_SHOW_ID)}>
          <Spacing size="S">
            <i className="id-icon" />
            <Text size="S" elementType="span">
              {t('remember_your_official_identification_1')}{' '}
              <Trans
                i18nKey="remember_your_official_identification_2"
                t={t}
                className="font-semibold inline"
                tOptions={{ context: brand }}
                components={{
                  blog: (
                    <a
                      href="https://blog.reservamos.mx/posts/requisitos-para-viajar-en-autobus/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      placeholder
                    </a>
                  ),
                }}
              />
            </Text>
          </Spacing>
        </When>

        <When condition={Boolean(features.SHOW_INFANTS_DISCLAIMER)}>
          <Spacing size="S">
            <i className="minor-icon" />
            <Text size="S">
              <InfantDisclaimerText />
            </Text>
          </Spacing>
          {features.SHOW_SECOND_INFANT_DISCLAIMER && (
            <Spacing size="S">
              <i className="minor-icon" />
              <Text size="S">
                <Trans
                  i18nKey="infant_disclaimer_2"
                  t={t}
                  values={{ context: brand }}
                  components={{
                    1: <strong />,
                    ul: <ul />,
                    li: <li />,
                    br: <br />,
                  }}
                />
              </Text>
            </Spacing>
          )}
        </When>

        <When condition={Boolean(features.SHOW_FOREIGN_DISCLAIMER)}>
          <Spacing size="S">
            <When
              condition={
                Boolean(features.SHOW_DOCUMENT_VALIDATION_DISCLAIMER) && someWithoutGeneralCategory
              }
            >
              <Icon type="CircleInfo" size="S" />
              <Spacing size="S" vertical>
                <Text size="S">{t('instructions.file_upload_validation_notice_1')}</Text>
                <Text size="S">{t('instructions.file_upload_validation_notice_2')}</Text>
                <Text size="S">{t('instructions.file_upload_validation_notice_3')}</Text>
              </Spacing>
            </When>
          </Spacing>
          <Spacing size="S">
            <Icon type="CircleInfo" size="S" />
            <Spacing size="S" vertical>
              <Text size="S">{t('foreign_disclaimer_1', { context: brand })}</Text>
              <Text size="S">{t('foreign_disclaimer_2', { context: brand })}</Text>
              <Text size="S">{t('foreign_disclaimer_3', { context: brand })}</Text>
            </Spacing>
          </Spacing>
        </When>

        <When condition={Boolean(features.DOCUMENT_PER_PASSENGER)}>
          <Spacing vertical size="S">
            <Spacing size="S">
              <Icon type="CircleInfo" size="S" />
              <Spacing size="XS" vertical>
                <Text size="S">{t('instructions.file_upload')}</Text>
              </Spacing>
            </Spacing>
          </Spacing>
        </When>
      </Spacing>
    </When>
  );
};

export default PassengersStatements;
